// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2024 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { OpportunityType } from "~datatypes";

import UserStore from "../../../../../RootStore/UserStore";
import { IApiOpportunityConfiguration, OpportunityConfiguration } from "../..";
import { ApiOpportunityConfiguration } from "../ApiOpportunityConfigurationImpl";
import { PastFTRDConfiguration } from "./PastFTRDConfiguration";
import { UsAzReleaseToDTPConfiguration } from "./UsAz/UsAzReleaseToDTPConfiguration";
import { UsAzReleaseToTPRConfiguration } from "./UsAz/UsAzReleaseToTPRConfiguration";
import { UsAzTransferToAdministrativeSupervisionConfiguration } from "./UsAz/UsAzTransferToAdministrativeSupervisionConfiguration";
import { UsAzTransferToAdministrativeSupervisionV2Configuration } from "./UsAz/UsAzTransferToAdministrativeSupervisionV2Configuration";
import { UsIaEarlyDischargeConfiguration } from "./UsIa/UsIaEarlyDischargeConfiguration";
import { UsIaSupervisionLevelDowngradeConfiguration } from "./UsIa/UsIaSupervisionLevelDischargeConfiguration";
import { LSUConfiguration } from "./UsId/LSUConfiguration";
import { UsIdCRCConfiguration } from "./UsId/UsIdCRCConfiguration";
import { UsIdCustodyLevelDowngradeConfiguration } from "./UsId/UsIdCustodyLevelDowngradeConfiguration";
import { UsIdEarnedDischargeConfiguration } from "./UsId/UsIdEarnedDischargeConfiguration";
import { UsIdOverdueFaceToFaceContactConfiguration } from "./UsId/UsIdOverdueFaceToFaceContactConfiguration";
import { UsIdTransferToCRCLikeBedConfiguration } from "./UsId/UsIdTransferToCRCLikeBedConfiguration";
import { UsMiAddInPersonSecurityClassificationCommitteeReviewV2Configuration } from "./UsMi/SCCReview/UsMiAddInPersonSecurityClassificationCommitteeReviewV2Configuration";
import { UsMiSecurityClassificationCommitteeReviewV2Configuration } from "./UsMi/SCCReview/UsMiSecurityClassificationCommitteeReviewV2Configuration";
import { UsMiWardenInPersonSecurityClassificationCommitteeReviewV2Configuration } from "./UsMi/SCCReview/UsMiWardenInPersonSecurityClassificationCommitteeReviewV2Configuration";
import { UsMiCustodyLevelDowngradeConfiguration } from "./UsMi/UsMiCustodyLevelDowngradeConfiguration";
import { UsMiEarlyDischargeConfiguration } from "./UsMi/UsMiEarlyDischargeConfiguration";
import { UsMiMinimumTelephoneReportingConfiguration } from "./UsMi/UsMiMinimumTelephoneReporting";
import { usMiPastFTRDConfiguration } from "./UsMi/UsMiPastFTRD";
import { UsMoOverdueRestrictiveHousingConfiguration } from "./UsMo/UsMoOverdueRestrictiveHousingConfiguration";
import { UsMoWorkReleaseConfiguration } from "./UsMo/UsMoWorkReleaseConfiguration";
import { UsNcCreditReductionReviewConfiguration } from "./UsNc/UsNcCreditReductionReviewConfiguration";
import { UsNdTransferToMinFacility } from "./UsNd/UsNdTransferToMinFacility";
import { UsNeGoodTimeRestorationConfiguration } from "./UsNe/UsNeGoodTimeRestorationConfiguration";
import { UsOrEarnedDischargeSentenceConfiguration } from "./UsOr/UsOrEarnedDischargeSentenceConfiguration";
import { UsPaSupervisionConfiguration } from "./UsPa/UsPaSupervisionBaseConfiguration";
import { UsTn2026ClassificationConfiguration } from "./UsTn/UsTn2026ClassificationConfiguration";
import { UsTnBaselineClassificationConfiguration } from "./UsTn/UsTnBaselineClassificationConfiguration";
import { UsTnCompliantReporting2025PolicyConfiguration } from "./UsTn/UsTnCompliantReporting2025PolicyConfiguration";
import { UsTnCustodyLevelDowngrade2026PolicyConfiguration } from "./UsTn/UsTnCustodyLevelDowngrade2026PolicyConfiguration";
import { UsTnExpirationConfiguration } from "./UsTn/UsTnExpirationConfiguration";
import { UsTnSuspensionofDirectSupervisionConfiguration } from "./UsTn/UsTnSuspensionofDirectSupervisionConfiguration";
import { UsTxAnnualReportStatusConfiguration } from "./UsTx/UsTxAnnualReportStatusConfiguration";
import { UsTxArsErsV2Configuration } from "./UsTx/UsTxArsErsV2Configuration";
import { UsTxEarlyReleaseFromSupervisionConfiguration } from "./UsTx/UsTxEarlyReleaseFromSupervision";
import { UsUtEarlyTerminationConfiguration } from "./UsUt/UsUtEarlyTerminationConfiguration";

const customOpportunityConfigurations: Partial<
  Record<OpportunityType, typeof ApiOpportunityConfiguration>
> = {
  // Arizona
  usAzTransferToAdministrativeSupervision:
    UsAzTransferToAdministrativeSupervisionConfiguration,
  usAzTransferToAdministrativeSupervisionV2:
    UsAzTransferToAdministrativeSupervisionV2Configuration,
  usAzReleaseToDTP: UsAzReleaseToDTPConfiguration,
  usAzReleaseToTPR: UsAzReleaseToTPRConfiguration,

  // Iowa
  usIaCompleteSupervisionLevelDowngrade:
    UsIaSupervisionLevelDowngradeConfiguration,
  usIaEarlyDischarge: UsIaEarlyDischargeConfiguration,

  // Idaho
  pastFTRD: PastFTRDConfiguration,
  earnedDischarge: UsIdEarnedDischargeConfiguration,
  LSU: LSUConfiguration,
  usIdCRCResidentWorker: UsIdCRCConfiguration,
  usIdCRCWorkRelease: UsIdCRCConfiguration,
  usIdExpandedCRC: UsIdCRCConfiguration,
  usIdCustodyLevelDowngrade: UsIdCustodyLevelDowngradeConfiguration,
  usIdOverdueFaceToFaceContact: UsIdOverdueFaceToFaceContactConfiguration,
  usIdTransferToCRCLikeBed: UsIdTransferToCRCLikeBedConfiguration,

  // Michigan
  usMiCustodyLevelDowngrade: UsMiCustodyLevelDowngradeConfiguration,
  usMiEarlyDischarge: UsMiEarlyDischargeConfiguration,
  usMiMinimumTelephoneReporting: UsMiMinimumTelephoneReportingConfiguration,
  usMiSecurityClassificationCommitteeReviewV2:
    UsMiSecurityClassificationCommitteeReviewV2Configuration,
  usMiAddInPersonSecurityClassificationCommitteeReviewV2:
    UsMiAddInPersonSecurityClassificationCommitteeReviewV2Configuration,
  usMiWardenInPersonSecurityClassificationCommitteeReviewV2:
    UsMiWardenInPersonSecurityClassificationCommitteeReviewV2Configuration,
  usMiPastFTRD: usMiPastFTRDConfiguration,

  // Missouri
  usMoOutsideClearance: UsMoWorkReleaseConfiguration,
  usMoOverdueRestrictiveHousingInitialHearing:
    UsMoOverdueRestrictiveHousingConfiguration,
  usMoOverdueRestrictiveHousingRelease:
    UsMoOverdueRestrictiveHousingConfiguration,
  usMoOverdueRestrictiveHousingReviewHearing:
    UsMoOverdueRestrictiveHousingConfiguration,
  usMoWorkRelease: UsMoWorkReleaseConfiguration,

  // North Carolina
  usNcCreditReductionReview: UsNcCreditReductionReviewConfiguration,

  // North Dakota
  // This is a non-constructed opportunity that is being customized
  ["usNdTransferToMinFacility" as OpportunityType]: UsNdTransferToMinFacility,

  // Nebraska
  usNeGoodTimeRestoration: UsNeGoodTimeRestorationConfiguration,

  // Oregon
  usOrEarnedDischargeSentence: UsOrEarnedDischargeSentenceConfiguration,

  // Pennsylvania
  usPaAdminSupervision: UsPaSupervisionConfiguration,
  usPaSpecialCircumstancesSupervision: UsPaSupervisionConfiguration,

  // Tennessee
  usTnCompliantReporting2025Policy:
    UsTnCompliantReporting2025PolicyConfiguration,
  usTnExpiration: UsTnExpirationConfiguration,
  usTnSuspensionOfDirectSupervision:
    UsTnSuspensionofDirectSupervisionConfiguration,
  usTnAnnualReclassification: UsTnBaselineClassificationConfiguration,
  usTnCustodyLevelDowngrade: UsTnBaselineClassificationConfiguration,
  usTnInitialClassification: UsTnBaselineClassificationConfiguration,
  usTnAnnualReclassification2026Policy: UsTn2026ClassificationConfiguration,
  usTnCustodyLevelDowngrade2026Policy:
    UsTnCustodyLevelDowngrade2026PolicyConfiguration,
  usTnInitialClassification2026Policy: UsTn2026ClassificationConfiguration,
  usTnSpecialCustodyLevelUpgrade2026Policy: UsTn2026ClassificationConfiguration,
  usTnTrusteeTransfer: UsTn2026ClassificationConfiguration,
  usTnSeriousMisconductUpgrade: UsTn2026ClassificationConfiguration,
  usTnBiannualOther: UsTn2026ClassificationConfiguration,
  usTnAnnualReclassification2026PolicyV2: UsTn2026ClassificationConfiguration,
  usTnCustodyLevelDowngrade2026PolicyV2: UsTn2026ClassificationConfiguration,
  usTnSpecialCustodyLevelUpgrade2026PolicyV2:
    UsTn2026ClassificationConfiguration,
  usTnTrusteeTransferV2: UsTn2026ClassificationConfiguration,
  usTnSeriousMisconductUpgradeV2: UsTn2026ClassificationConfiguration,
  usTnBiannualOtherV2: UsTn2026ClassificationConfiguration,

  // Texas
  usTxAnnualReportStatus: UsTxAnnualReportStatusConfiguration,
  usTxAnnualReportStatusV2: UsTxArsErsV2Configuration,
  usTxEarlyReleaseFromSupervision: UsTxEarlyReleaseFromSupervisionConfiguration,
  usTxEarlyReleaseFromSupervisionV2: UsTxArsErsV2Configuration,

  // Utah
  usUtEarlyTermination: UsUtEarlyTerminationConfiguration,
};

export function apiOpportunityConfigurationFactory(
  opportunityType: OpportunityType,
  configurationObject: IApiOpportunityConfiguration,
  userStore: UserStore,
): OpportunityConfiguration {
  const ConfigClass =
    customOpportunityConfigurations[opportunityType] ??
    ApiOpportunityConfiguration;
  return new ConfigClass(configurationObject, userStore);
}
